import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
  useMotionValueEvent,
  useMotionTemplate,
  type MotionValue,
} from "framer-motion";
import { TrustCarousel } from "@/components/TrustCarousel";
import { company } from "@/content/site";
import { cn } from "@/lib/utils";

/**
 * Terminal-style scroll-driven hero ("scroll = play head"). The section pins to
 * the viewport while a tall track scrolls; scroll progress (0->1) scrubs a
 * pre-rendered 30s master video (drive -> LiDAR morph -> wireframe fleet ->
 * North America ops network) by easing `currentTime` toward the scroll target,
 * and a 7-beat ad script builds word-by-word in sync with the footage.
 *
 * Desktop only — mobile and prefers-reduced-motion get a clean static hero
 * (video scrubbing is unreliable on touch browsers and heavy on data).
 * Master video + poster live in /public/brand; see docs/CHANGELOG.md.
 */
export function ScrollHero() {
  const reduce = useReducedMotion();
  const isDesktop = useIsDesktop();

  // Desktop (wide + fine pointer): full-screen, scroll-scrubbed cinematic hero.
  // Everything else (phones, tablets, reduced-motion) gets a COMPACT hero — the
  // full truck plus the headline sized to their content so the real page flows
  // right below, instead of a full-screen letterbox that leaves big black gaps
  // (the ultra-wide footage can't fill a tall portrait screen without cropping).
  if (isDesktop && !reduce) {
    return <MotionHero />;
  }
  return <CompactHero reduce={Boolean(reduce)} />;
}

interface BeatDef {
  words: { t: string; accent?: boolean }[];
  sub: string;
  /** [enter, exit] in scroll-progress space (0..1). */
  range: [number, number];
}

// The video scrubs across the first PLAY fraction of the scroll track; the
// remaining tail is a static "end card" hold (final network frame + brand + CTA
// locked on screen) before the hero unpins.
const PLAY = 0.85;

// One ad beat per video segment, scaled into the PLAY window (four quarters):
//   A drive at dusk | B real->digital morph | C wireframe fleet | D NA network
const BEATS: BeatDef[] = [
  { range: [0.0, 0.2125], sub: "Around-the-clock tanker freight.", words: [{ t: "Rolling" }, { t: "day" }, { t: "and" }, { t: "night.", accent: true }] },
  { range: [0.2125, 0.425], sub: "Track every load in real time.", words: [{ t: "Trucking" }, { t: "meets" }, { t: "technology.", accent: true }] },
  { range: [0.425, 0.6375], sub: "Live GPS, ELD, and telemetry across the fleet.", words: [{ t: "See" }, { t: "every" }, { t: "mile.", accent: true }] },
  { range: [0.6375, 0.83], sub: "48 states and into Canada.", words: [{ t: "Everywhere" }, { t: "in" }, { t: "North", accent: true }, { t: "America.", accent: true }] },
];

function MotionHero() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const targetTime = useRef(0);
  const durationRef = useRef(0);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  // Map scroll -> target video time; a rAF loop eases currentTime toward it so
  // the footage glides (rather than snapping) as you scroll.
  useMotionValueEvent(scrollYProgress, "change", (p) => {
    // Video completes by PLAY, then holds on its final frame for the end card.
    const t = Math.min(p, PLAY) / PLAY;
    targetTime.current = Math.max(0, Math.min(1, t)) * durationRef.current;
  });

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let raf = 0;
    let running = true;

    const onMeta = () => {
      durationRef.current = v.duration || 0;
      v.pause();
      try {
        v.currentTime = 0.001;
      } catch {
        /* ignore */
      }
    };
    if (v.readyState >= 1) onMeta();
    v.addEventListener("loadedmetadata", onMeta);

    const tick = () => {
      if (!running) return;
      const dur = durationRef.current;
      if (dur) {
        const cur = v.currentTime;
        const diff = targetTime.current - cur;
        if (Math.abs(diff) > 0.012) {
          try {
            v.currentTime = cur + diff * 0.18;
          } catch {
            /* ignore mid-seek errors */
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      v.removeEventListener("loadedmetadata", onMeta);
    };
  }, []);

  const barScaleX = scrollYProgress;
  const hintOpacity = useTransform(scrollYProgress, [0, 0.05], [1, 0]);

  // Final CTA block resolves over the network frame, then stays locked through
  // the end-card hold (no fade-out) until the hero unpins at progress 1.
  const ctaOpacity = useTransform(scrollYProgress, [0.84, 0.9], [0, 1]);

  return (
    // Taller track = the same scrub/beats spread over more scroll distance, so the
    // hero advances more slowly and the headlines are easier to read.
    <section ref={sectionRef} className="relative h-[600vh] bg-black text-white">
      <div className="sticky top-0 h-screen overflow-hidden">
        <video
          ref={videoRef}
          // Letterbox (contain) so the full ultra-wide (2.33:1) scene stays
          // visible on the wide desktop viewport it nearly fills.
          className="absolute inset-0 h-full w-full object-contain object-top"
          src="/brand/hero-master.mp4"
          poster="/brand/hero-master-poster.jpg"
          muted
          playsInline
          preload="auto"
          tabIndex={-1}
          aria-hidden="true"
        />

        {/* Legibility scrims */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/10" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/55 via-transparent to-transparent" />

        {/* Top progress bar */}
        <motion.div className="absolute inset-x-0 top-0 h-[3px] origin-left bg-accent-500" style={{ scaleX: barScaleX }} />

        {/* Text stage — pinned to the bottom over the full-bleed video. */}
        <div className="absolute inset-0 mx-auto flex max-w-[1280px] flex-col justify-end px-6 pb-28">
          <div className="relative min-h-[3.6em]">
            {BEATS.map((b, i) => (
              <Beat key={i} progress={scrollYProgress} def={b} />
            ))}

            {/* Beat 7 — brand close (lives with the CTA so it can resolve together) */}
            <motion.div className="absolute inset-x-0 bottom-0" style={{ opacity: ctaOpacity }}>
              <div className="text-[clamp(2.5rem,7vw,5.5rem)] font-bold leading-[0.98] tracking-tight drop-shadow-[0_2px_18px_rgba(0,0,0,0.6)]">
                Bull <span className="text-accent-500">&amp;</span> Bear Co.
              </div>
              <p className="mt-3 max-w-xl text-base text-brand-100 sm:text-lg">Move your freight with a carrier that answers.</p>
            </motion.div>
          </div>
        </div>

        {/* Scroll hint */}
        <motion.div className="pointer-events-none absolute inset-x-0 bottom-6 flex justify-center" style={{ opacity: hintOpacity }}>
          <span className="text-[0.7rem] font-medium uppercase tracking-[0.35em] text-white/60">Scroll to explore</span>
        </motion.div>
      </div>
    </section>
  );
}

/** One ad beat: headline builds word-by-word, holds, then lifts away. */
function Beat({ progress, def }: { progress: MotionValue<number>; def: BeatDef }) {
  const [a, b] = def.range;
  const span = b - a;
  // Hold the line visible across most of the beat (fast in, long hold, late fade).
  const opacity = useTransform(progress, [a, a + span * 0.07, b - span * 0.1, b], [0, 1, 1, 0]);
  const y = useTransform(progress, [b - span * 0.12, b], ["0%", "-24%"]);
  const buildStart = a + span * 0.04;
  const buildEnd = a + span * 0.32;
  // Fractional points keep the input range strictly ascending for any span
  // (useTransform throws on a non-monotonic range).
  const subOpacity = useTransform(
    progress,
    [a + span * 0.28, a + span * 0.42, b - span * 0.1, b - span * 0.02],
    [0, 1, 1, 0],
  );

  return (
    <motion.div className="absolute inset-x-0 bottom-0" style={{ opacity, y }}>
      <div className="text-[clamp(2.5rem,7vw,5.5rem)] font-bold leading-[0.98] tracking-tight drop-shadow-[0_2px_18px_rgba(0,0,0,0.6)]">
        <Phrase progress={progress} start={buildStart} end={buildEnd} words={def.words} />
      </div>
      <motion.p className="mt-3 max-w-xl text-base text-brand-100 sm:text-lg" style={{ opacity: subOpacity }}>
        {def.sub}
      </motion.p>
    </motion.div>
  );
}

interface PhraseWord {
  t: string;
  accent?: boolean;
}

/** Builds a phrase word-by-word as scroll progress moves from start -> end. */
function Phrase({
  progress,
  words,
  start,
  end,
}: {
  progress: MotionValue<number>;
  words: PhraseWord[];
  start: number;
  end: number;
}) {
  const span = end - start;
  return (
    <span className="inline">
      {words.map((w, i) => {
        const ws = start + (i / words.length) * span * 0.7;
        const we = Math.min(ws + span * 0.42, end);
        return (
          <ScrollWord key={i} progress={progress} start={ws} end={we} accent={w.accent}>
            {w.t}
          </ScrollWord>
        );
      })}
    </span>
  );
}

function ScrollWord({
  progress,
  start,
  end,
  accent,
  children,
}: {
  progress: MotionValue<number>;
  start: number;
  end: number;
  accent?: boolean;
  children: ReactNode;
}) {
  const opacity = useTransform(progress, [start, end], [0, 1]);
  const y = useTransform(progress, [start, end], ["0.5em", "0em"]);
  const blurPx = useTransform(progress, [start, end], [10, 0]);
  const filter = useMotionTemplate`blur(${blurPx}px)`;
  return (
    <motion.span
      className={cn("mr-[0.25em] inline-block", accent ? "text-accent-400" : "text-white")}
      style={{ opacity, y, filter }}
    >
      {children}
    </motion.span>
  );
}

/** Reactive desktop check — scrubbing is desktop-only. */
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px) and (pointer: fine)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isDesktop;
}

/**
 * Compact hero for phones/tablets. The footage stays in a full-width band at its
 * own 2.33:1 ratio (whole truck, no side-cropping), and the band itself sticks
 * while it scroll-scrubs. The section has a short internal scrub track, but its
 * bottom is overlapped by the following page content so the extra distance does
 * not show up as a blank black tail on tall phone screens.
 * Reduced-motion users get a static version with no scroll-jacking.
 */
function CompactHero({ reduce }: { reduce: boolean }) {
  if (reduce) return <StaticCompactHero />;
  return <PinnedCompactHero />;
}

function StaticCompactHero() {
  return (
    <section className="relative bg-black text-white">
      <img
        src="/brand/hero-master-poster.jpg"
        alt=""
        aria-hidden="true"
        className="block aspect-[854/366] w-full object-cover object-center"
      />
      <div className="mx-auto max-w-[1280px] px-6 pb-10 pt-6">
        <h1 className="text-[clamp(2rem,8.5vw,3.25rem)] font-bold leading-[1.04] tracking-tight">
          Rolling <span className="text-accent-400">day and night.</span>
          <br />
          Everywhere in <span className="text-accent-400">North America.</span>
        </h1>
        <p className="mt-4 max-w-xl text-base text-brand-100">
          {company.name} is a Midwest-based tanker carrier running liquid bulk across all 48 states and into Canada.
        </p>
      </div>
    </section>
  );
}

function PinnedCompactHero() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const bgVideoRef = useRef<HTMLVideoElement>(null);
  const targetTime = useRef(0);
  const durationRef = useRef(0);

  // Mobile mirrors the desktop "pin the stage, scroll = play head" behavior: the
  // truck video is scroll-scrubbed (not autoplaying), with a blurred copy filling
  // the portrait space and the headline beats animating under it.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const t = Math.min(p, PLAY) / PLAY;
    targetTime.current = Math.max(0, Math.min(1, t)) * durationRef.current;
  });

  const ctaOpacity = useTransform(scrollYProgress, [0.84, 0.9], [0, 1]);
  const hintOpacity = useTransform(scrollYProgress, [0, 0.05], [1, 0]);

  useEffect(() => {
    const v = videoRef.current;
    const bg = bgVideoRef.current;
    if (!v) return;
    let raf = 0;
    let running = true;

    // Prime the videos: mobile browsers (esp. iOS Safari) won't repaint a paused
    // video when currentTime is set unless it has actually played first. A brief
    // muted play() -> pause() warms the decoder so seeks render while scrubbing.
    const prime = (el: HTMLVideoElement | null) => {
      if (!el) return;
      const p = el.play();
      if (p && typeof p.then === "function") {
        p.then(() => el.pause()).catch(() => {});
      } else {
        el.pause();
      }
    };

    const onMeta = () => {
      durationRef.current = v.duration || 0;
    };
    if (v.readyState >= 1) onMeta();
    v.addEventListener("loadedmetadata", onMeta);

    prime(v);
    prime(bg);
    // iOS only decodes inside a user gesture; re-prime once on first touch.
    const onFirstTouch = () => {
      prime(v);
      prime(bg);
    };
    window.addEventListener("touchstart", onFirstTouch, { once: true, passive: true });

    const tick = () => {
      if (!running) return;
      if (durationRef.current > 0) {
        const cur = v.currentTime;
        const diff = targetTime.current - cur;
        if (Math.abs(diff) > 0.01) {
          try {
            const nextTime = cur + diff * 0.18;
            v.currentTime = nextTime;
            if (bg) bg.currentTime = nextTime;
          } catch {
            /* ignore */
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      v.removeEventListener("loadedmetadata", onMeta);
      window.removeEventListener("touchstart", onFirstTouch);
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative h-[380svh] bg-black text-white">
      {/* Pin just below the floating site header (~4.25rem tall once scrolled)
          so the ultra-wide frames — especially the final map, which has content
          at the top edge — never slide up behind the header and get clipped.
          The tall track (380svh) keeps the scrub slow/readable on touch. */}
      <div className="sticky top-[4.25rem] h-[calc(100svh-4.25rem)] overflow-hidden bg-black">
        {/* Blurred moving fill: fills portrait space that the ultra-wide truck band cannot occupy. */}
        <video
          ref={bgVideoRef}
          className="absolute inset-0 h-full w-full scale-110 object-cover object-center opacity-75 blur-xl saturate-125"
          src="/brand/hero-master-mobile.mp4"
          poster="/brand/hero-master-poster.jpg"
          muted
          playsInline
          preload="auto"
          tabIndex={-1}
          aria-hidden="true"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/0 via-black/20 to-black/70" />

        {/* Foreground truck stays fully visible. */}
        <video
          ref={videoRef}
          className="absolute inset-x-0 top-0 z-10 aspect-[854/366] w-full object-contain object-center drop-shadow-[0_18px_45px_rgba(0,0,0,0.55)]"
          src="/brand/hero-master-mobile.mp4"
          poster="/brand/hero-master-poster.jpg"
          muted
          playsInline
          preload="auto"
          tabIndex={-1}
          aria-hidden="true"
        />

        <div className="absolute inset-x-0 top-[calc(42.86vw+1rem)] z-20 mx-auto max-w-[1280px] px-6">
          <div className="relative min-h-[10.5rem]">
          {BEATS.map((b, i) => (
            <Beat key={i} progress={scrollYProgress} def={b} />
          ))}

          <motion.div className="absolute inset-x-0 bottom-0" style={{ opacity: ctaOpacity }}>
            <div className="text-[clamp(2.5rem,7vw,5.5rem)] font-bold leading-[0.98] tracking-tight drop-shadow-[0_2px_18px_rgba(0,0,0,0.6)]">
              Bull <span className="text-accent-500">&amp;</span> Bear Co.
            </div>
            <p className="mt-3 max-w-xl text-base text-brand-100">Move your freight with a carrier that answers.</p>
          </motion.div>
          </div>
        </div>

        <motion.div className="pointer-events-none absolute inset-x-0 top-[calc(42.86vw+13.25rem)] z-20 flex justify-center" style={{ opacity: hintOpacity }}>
          <span className="text-[0.7rem] font-medium uppercase tracking-[0.35em] text-white/60">Scroll to explore</span>
        </motion.div>

        <div className="absolute inset-x-0 bottom-0 z-20">
          <div className="border-y border-white/10 bg-brand-800/95 px-6 py-3">
            <div className="mx-auto max-w-[1280px]">
              <TrustCarousel />
            </div>
          </div>
          <div className="bg-white px-6 py-8 text-brand-900">
            <div className="mx-auto max-w-[1280px]">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-accent-500">Why Bull &amp; Bear</div>
              <div className="text-[clamp(1.9rem,7vw,2.75rem)] font-semibold leading-tight">The carrier your freight deserves.</div>
              <p className="mt-3 max-w-xl text-sm text-brand-500">
                Visibility, accountability, and people you'll actually remember.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
